import { handle400Statuses, sendApiDownloadRequest, sendApiRequest, sendMultipartApiRequest } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';
import { parseContentDispositionFilename } from '@utils/documentVault';

// Financial Model Data Capture (fmdc). Wraps the Rails /api/v2/genai/fmdc surface plus the one v1
// upload route. Mirrors src/services/scoreBackfills.js and scoreSpecs.js: every function calls the
// api helper, logs, runs handle400Statuses, branches on a 2xx with data, and returns a flat
// `{ status, <payload>, error }` object — it never throws. The server's message for a 4xx lives in
// data.error, so that is preferred over the transport error.
//
// Everything is scoped server-side to the caller's custom client; the SPA never sends a client id.
// Admin-only routes answer 403 to members: callers gate on `can_admin` from fetchTenant and never
// call them speculatively.

const BASE_URL = 'v2/genai/fmdc'; // sendApiRequest prepends `${BASEURL}/api/`
const UPLOAD_URL = 'v1/genai/upload/excel';

export const RUN_STATUSES = {
  PENDING: 'pending',
  INVENTORY: 'inventory',
  HEADERS: 'headers',
  CLASSIFY: 'classify',
  NORMALIZE: 'normalize',
  VALIDATE: 'validate',
  REVIEW: 'review',
  LOADABLE: 'loadable',
  ERROR: 'error',
};

// The pipeline, in order. Everything after `validate` is an outcome rather than a stage.
export const RUN_STAGES = [
  RUN_STATUSES.PENDING,
  RUN_STATUSES.INVENTORY,
  RUN_STATUSES.HEADERS,
  RUN_STATUSES.CLASSIFY,
  RUN_STATUSES.NORMALIZE,
  RUN_STATUSES.VALIDATE,
];

export const RUN_TERMINAL_STATUSES = [RUN_STATUSES.REVIEW, RUN_STATUSES.LOADABLE, RUN_STATUSES.ERROR];

// Finished with output: the workbook and payload exist, the queue can be opened.
export const RUN_FINISHED_STATUSES = [RUN_STATUSES.REVIEW, RUN_STATUSES.LOADABLE];

// A stuck run (no progress after 6 h) is not terminal on the wire but there is nothing to wait for.
export const isRunTerminal = (run) => !!run && (RUN_TERMINAL_STATUSES.includes(run.status) || run.stuck === true);
export const isRunFinished = (run) => !!run && RUN_FINISHED_STATUSES.includes(run.status);

export const SETUP_STATUSES = { QUEUED: 'queued', RUNNING: 'running', SUCCEEDED: 'succeeded', FAILED: 'failed' };
export const SETUP_ACTIVE_STATUSES = [SETUP_STATUSES.QUEUED, SETUP_STATUSES.RUNNING];
export const isSetupActive = (setup) => !!setup && SETUP_ACTIVE_STATUSES.includes(setup.status) && !setup.stale;

export const TEMPLATE_RUN_STATUSES = { RUNNING: 'running', COMPLETE: 'complete', ERROR: 'error' };

export const LOAD_ACTIONS = {
  LOAD_VALUE: 'load_value',
  LOAD_ASSUMPTION: 'load_assumption',
  REVIEW_ONLY: 'review_only',
  PROPOSE_NEW_CATALOG_ITEM: 'propose_new_catalog_item',
  DO_NOT_LOAD_VARIANCE: 'do_not_load_variance',
};

export const REVIEWER_STATUSES = { PENDING: 'pending', MIXED: 'mixed', APPROVED: 'approved', REJECTED: 'rejected' };
export const CONFIDENCE = { HIGH: 'high', MEDIUM: 'medium', LOW: 'low' };
export const VALIDATION_STATUSES = { PASS: 'pass', WARNING: 'warning', FAIL: 'fail', NOT_CHECKED: 'not_checked' };

export const REVIEW_SUBJECTS = { EXTRACTION: 'extraction', UNMAPPED: 'unmapped' };
export const REVIEW_GROUP_STATUSES = { OPEN: 'open', ALL: 'all' };
export const REVIEW_ACTIONS = { APPROVE: 'approve', REJECT: 'reject', REQUEUE: 'requeue' };
export const UNMAPPED_REASON_PREFIX = 'unmapped:';

export const PROPOSAL_STATUSES = { PENDING: 'pending', APPROVED: 'approved', REJECTED: 'rejected' };
export const PROPOSAL_DECISIONS = { APPROVED: 'approved', REJECTED: 'rejected' };
export const CATALOG_ITEM_STATUSES = { ACTIVE: 'active', PROPOSED: 'proposed', RETIRED: 'retired' };

export const DOWNLOAD_KINDS = { WORKBOOK: 'workbook', PAYLOAD: 'payload' };

export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 500;
// The server dedupes ids and caps every decide route (review groups, extraction rows, proposals)
// at this many per call; the UI sends at most a page.
export const DECIDE_MAX_IDS = 500;

// Validated client-side so the documented 400s never happen.
export const ISSUER_PATTERN = /^[A-Za-z0-9 ._\-/]{1,64}$/;
export const TEMPLATE_VERSION_PATTERN = /^[A-Za-z0-9._-]{1,32}$/;
export const CATALOG_CODE_PATTERN = /^[a-z][a-z0-9_]*$/;
export const TEMPLATE_MAX_BYTES = 25 * 1024 * 1024;
export const TENANT_NAME_MAX = 100;

// The settings PATCH /tenant accepts, with the control each wants. `null` resets one to its default.
export const TENANT_SETTING_KEYS = [
  { key: 'model', kind: 'model', label: 'Model' },
  { key: 'llm_enabled', kind: 'boolean', label: 'LLM classification' },
  { key: 'knn_enabled', kind: 'boolean', label: 'kNN matching' },
  { key: 'max_llm_calls_base', kind: 'number', label: 'Max LLM calls (base)' },
  { key: 'max_llm_calls_per_sheet', kind: 'number', label: 'Max LLM calls per sheet' },
  { key: 'max_llm_tokens', kind: 'number', label: 'Max LLM tokens' },
  { key: 'classifier_batch_size', kind: 'number', label: 'Classifier batch size' },
];

// Distinguishable per-status fallbacks, used only when the server sends no data.error.
const createErrorFallback = (status) => {
  if (status === 400) return 'The request was rejected. Check the fields and try again.';
  if (status === 403) return 'Admin access is required for this action.';
  if (status === 404) return 'Not found. It may belong to another client, or data capture may be switched off.';
  if (status === 409) return 'That conflicts with the current state. Refresh and try again.';
  if (status === 422) return 'The server rejected these values.';
  if (status === 502) return 'The extraction engine is unreachable. Try again shortly.';
  return 'Something went wrong. Please try again.';
};

// The server's own message first; then the status-specific fallback; then the transport error,
// which is only ever the generic sentence and would otherwise hide a 413 or 504 whose body was
// not JSON.
const errorFrom = (data, error, status, fallbacks) =>
  data?.error || (fallbacks && fallbacks[status]) || error || createErrorFallback(status);

// A route segment from a value that may have come off the URL already decoded (`useParams`), so
// `1/../tenant` cannot walk the path.
const seg = (value) => encodeURIComponent(String(value));

const isSuccess = (status) => typeof status === 'number' && status >= 200 && status < 300;

// `?a=b&c=d` from a plain object; empty, null and undefined values are left out so a filter that is
// "not set" is structurally absent rather than sent as the string "null".
export const toQueryString = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

// One door for every JSON call. Returns the raw { status, data, error } so each exported function
// can pick its payload out and name it.
const request = async (method, path, body) => {
  const url = `${BASE_URL}/${path}`;
  const { status, data, error } = await sendApiRequest(method, url, body);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);
  return { status, data, error };
};

// `unwrap` names a wrapper key the server may put around the payload (`{ item }`, `{ run }`); when
// the body carries it the inner object is returned, otherwise the body itself is.
const pick = (result, key, fallbackValue, fallbacks, unwrap) => {
  const { status, data, error } = result;
  if (isSuccess(status) && data) {
    const payload = unwrap && data[unwrap] !== undefined ? data[unwrap] : data;
    return { status, [key]: payload, error: null };
  }
  return { status, [key]: fallbackValue, error: errorFrom(data, error, status, fallbacks) };
};

// ---- tenant ------------------------------------------------------------------------------------

// GET /tenant — works before setup (tenant is then null) and while fmdc is switched off.
export const fetchTenant = async () => {
  const { status, data, error } = await request('get', 'tenant');
  if (isSuccess(status) && data) {
    return { status, tenant: data.tenant || null, canAdmin: data.can_admin === true, error: null };
  }
  return { status, tenant: null, canAdmin: false, error: errorFrom(data, error, status) };
};

const SETUP_FALLBACKS = {
  409: 'A setup is already queued or running. Wait for it to finish.',
  400: 'The template was rejected. Check the file and the version.',
};

// POST /tenant/setup (admin, multipart). 202 with { setup }; then poll fetchTenant for setup.status.
export const setupTenant = async ({ template, templateVersion, name, embedCatalog }) => {
  const formData = new FormData();
  formData.append('template', template);
  formData.append('template_version', templateVersion);
  if (name) formData.append('name', name);
  if (embedCatalog === false) formData.append('embed_catalog', 'false');

  const url = `${BASE_URL}/tenant/setup`;
  const { status, data, error } = await sendMultipartApiRequest('POST', url, formData);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);
  if (isSuccess(status) && data) {
    return { status, setup: data.setup || null, error: null };
  }
  return { status, setup: null, error: errorFrom(data, error, status, SETUP_FALLBACKS) };
};

const PATCH_TENANT_FALLBACKS = {
  409: 'Data capture cannot be switched on without a catalog, or while a setup is running.',
  404: 'The client has not been set up yet.',
};

// PATCH /tenant (admin). Body: { name?, enabled?, settings? }.
export const updateTenant = async (patch) => {
  const result = await request('patch', 'tenant', patch);
  const { status, data, error } = result;
  if (isSuccess(status) && data) {
    return { status, tenant: data.tenant || null, error: null };
  }
  return { status, tenant: null, error: errorFrom(data, error, status, PATCH_TENANT_FALLBACKS) };
};

// GET /vocabularies — the dropdown values for correction, mapping and catalog forms, keyed by list.
export const fetchVocabularies = async () => pick(await request('get', 'vocabularies'), 'vocabularies', null);

// ---- upload --------------------------------------------------------------------------------------

export const UPLOAD_EXTENSIONS = ['xlsx', 'xlsm', 'xls'];

export const fileExtensionOf = (file) => {
  const name = String(file?.name || '');
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
};

// The file goes under a field named after its extension — `xlsx`, `xlsm` or `xls` — which is what
// the upload route keys on (and the same rule useUploadFormFiles applies to every other type).
export const buildUploadFormData = ({ file, name, issuerId, issuerName }) => {
  const formData = new FormData();
  formData.append(fileExtensionOf(file), file);
  formData.append('name', name);
  formData.append('fmdc', 'true');
  if (issuerId) formData.append('fmdc_issuer_id', issuerId);
  if (issuerName) formData.append('fmdc_issuer_name', issuerName);
  return formData;
};

const UPLOAD_FALLBACKS = {
  413: 'File is too large.',
  400: 'The upload was rejected. Check the file and its name.',
};

// POST /api/v1/genai/upload/excel with fmdc=true. 200 for both a new run and a duplicate — the
// duplicate carries `duplicate: true` and the existing run id, and is not an error.
export const uploadModel = async ({ file, name, issuerId, issuerName }) => {
  const formData = buildUploadFormData({ file, name, issuerId, issuerName });
  const { status, data, error } = await sendMultipartApiRequest('POST', UPLOAD_URL, formData);
  logNetRequest(UPLOAD_URL, status, data, error);
  handle400Statuses(status);
  if (isSuccess(status) && data) {
    return {
      status,
      docId: data.doc_id ?? null,
      runId: data.fmdc_run_id ?? null,
      duplicate: data.duplicate === true,
      message: data.message || null,
      error: null,
    };
  }
  // The multipart helper returns { error } with NO status on a non-JSON body (a proxy's HTML 413,
  // say). A missing status is a failure, never a success.
  return {
    status: status ?? null,
    docId: null,
    runId: null,
    duplicate: false,
    message: null,
    error: errorFrom(data, error, status, UPLOAD_FALLBACKS),
  };
};

// ---- runs ----------------------------------------------------------------------------------------

// GET /runs — newest first, paged.
export const fetchRuns = async ({ status, issuerId, limit = DEFAULT_PAGE_SIZE, offset = 0 } = {}) => {
  const result = await request('get', `runs${toQueryString({ status, issuer_id: issuerId, limit, offset })}`);
  if (isSuccess(result.status) && result.data) {
    return { status: result.status, runs: result.data.runs || [], total: result.data.total ?? 0, error: null };
  }
  return { status: result.status, runs: [], total: 0, error: errorFrom(result.data, result.error, result.status) };
};

// GET /runs/:id — the list entry plus validation_summary, sheet_inventory, stage_timings, source.
export const fetchRun = async (runId) =>
  pick(await request('get', `runs/${seg(runId)}`), 'run', null, undefined, 'run');

const RERUN_FALLBACKS = {
  409: 'Only a finished, failed or stuck run can be rerun.',
  400: 'The issuer is malformed: 1 to 64 characters, letters, digits, space, . _ - or /.',
  502: 'The extraction engine refused the rerun. The run keeps its previous status.',
};

// POST /runs/:id/rerun — 202 with the run (now pending). Setting the issuer here is how a run
// uploaded without one gets one.
export const rerunRun = async (runId, { issuerId, issuerName } = {}) => {
  const body = {};
  if (issuerId) body.issuer_id = issuerId;
  if (issuerName) body.issuer_name = issuerName;
  const result = await request('post', `runs/${seg(runId)}/rerun`, Object.keys(body).length ? body : undefined);
  return pick(result, 'run', null, RERUN_FALLBACKS, 'run');
};

// GET /runs/:id/validation — the rules as a list.
export const fetchRunValidation = async (runId) => {
  const result = await request('get', `runs/${seg(runId)}/validation`);
  if (isSuccess(result.status) && result.data) {
    return { status: result.status, rules: result.data.rules || [], error: null };
  }
  return { status: result.status, rules: [], error: errorFrom(result.data, result.error, result.status) };
};

// GET /runs/:id/static_info — { template_run: { id, status }, fields: [...] }.
export const fetchRunStaticInfo = async (runId) => {
  const result = await request('get', `runs/${seg(runId)}/static_info`);
  if (isSuccess(result.status) && result.data) {
    return {
      status: result.status,
      templateRun: result.data.template_run || null,
      fields: result.data.fields || [],
      error: null,
    };
  }
  return {
    status: result.status,
    templateRun: null,
    fields: [],
    error: errorFrom(result.data, result.error, result.status),
  };
};

export const SOURCE_WINDOW_MAX_ROWS = 60;
export const SOURCE_WINDOW_MAX_COLS = 40;

// GET /runs/:id/source_cells — the model's cells in a window (max 60 rows × 40 columns).
export const fetchSourceCells = async (runId, { sheet, rowFrom, rowTo, colFrom, colTo }) => {
  const query = toQueryString({ sheet, row_from: rowFrom, row_to: rowTo, col_from: colFrom, col_to: colTo });
  return pick(await request('get', `runs/${seg(runId)}/source_cells${query}`), 'window', null, {
    404: 'The run has no sheet by that name.',
    400: 'That window is larger than the server allows.',
  });
};

const DOWNLOAD_FALLBACKS = {
  409: 'Not ready yet — the run is still finishing.',
  422: 'The file could not be rendered.',
  // The load balancer in front of Rails idles out at 60 s while the render itself is allowed 300 s,
  // so a large model can be cut off mid-render. Nothing is wrong with the run.
  504: 'The download timed out at the gateway. Try again; a large model can take longer than it allows.',
};

const readDownloadError = async (response, status) => {
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }
  return errorFrom(data, null, status, DOWNLOAD_FALLBACKS);
};

// GET /runs/:id/workbook or /payload. Returns the raw bytes as a blob plus the server's filename.
// Mirrors downloadUserDoc in services/docs.js: the download helper hands back a raw Response (or a
// bare { error } when it threw), so the status is tested by type.
export const downloadRunFile = async (runId, kind, { signal } = {}) => {
  const path = kind === DOWNLOAD_KINDS.PAYLOAD ? 'payload' : 'workbook';
  const url = `${BASE_URL}/runs/${seg(runId)}/${path}`;
  let response;
  try {
    response = await sendApiDownloadRequest(url, { signal });
  } catch (e) {
    if (e?.name === 'AbortError') return { ok: false, canceled: true };
    logNetRequest(url, null, e);
    return { ok: false, error: createErrorFallback() };
  }

  if (!response || typeof response.status !== 'number') {
    return { ok: false, error: response?.error || createErrorFallback() };
  }

  const { status } = response;
  logNetRequest(url, status);
  handle400Statuses(status);

  if (status === 200) {
    let blob;
    try {
      blob = await response.blob();
    } catch (e) {
      if (e?.name === 'AbortError') return { ok: false, canceled: true };
      return { ok: false, status, error: createErrorFallback() };
    }
    const fallbackName = kind === DOWNLOAD_KINDS.PAYLOAD ? `run-${runId}-payload.json` : `run-${runId}-workbook.xlsx`;
    const filename = parseContentDispositionFilename(response.headers?.get('Content-Disposition')) || fallbackName;
    return { ok: true, status, blob, filename };
  }

  return { ok: false, status, error: await readDownloadError(response, status) };
};

// ---- review --------------------------------------------------------------------------------------

// GET /review_groups?run_id=… — one group per spreadsheet row, both subjects.
export const fetchReviewGroups = async ({
  runId,
  status,
  reason,
  sheet,
  subject,
  limit = DEFAULT_PAGE_SIZE,
  offset = 0,
}) => {
  const query = toQueryString({ run_id: runId, status, reason, sheet, subject, limit, offset });
  const result = await request('get', `review_groups${query}`);
  if (isSuccess(result.status) && result.data) {
    return { status: result.status, groups: result.data.groups || [], total: result.data.total ?? 0, error: null };
  }
  return { status: result.status, groups: [], total: 0, error: errorFrom(result.data, result.error, result.status) };
};

const DECIDE_FALLBACKS = {
  400: 'One of the selected rows cannot be decided here (an unmapped row, or a row from another run).',
  404: 'One of the selected rows no longer exists on this run.',
};

// POST /review_groups/decide — all groups or none. The response carries a per-group `results` list
// whose `mapping_saved` / `mapping_note` the queue shows next to each row.
export const decideReviewGroups = async ({ runId, groupIds, action, reason }) => {
  const body = { run_id: runId, group_ids: groupIds, action };
  if (reason) body.reason = reason;
  return pick(await request('post', 'review_groups/decide', body), 'result', null, DECIDE_FALLBACKS);
};

// GET /runs/:id/extraction_rows — cell-level rows with the Extraction_Values columns.
export const fetchExtractionRows = async (runId, filters = {}) => {
  const {
    sheet,
    rowIdx,
    section,
    item,
    validationStatus,
    confidence,
    reviewerStatus,
    loadAction,
    warningCode,
    q,
    sort,
    limit = DEFAULT_PAGE_SIZE,
    offset = 0,
  } = filters;
  const query = toQueryString({
    sheet,
    row_idx: rowIdx,
    section,
    item,
    validation_status: validationStatus,
    confidence,
    reviewer_status: reviewerStatus,
    load_action: loadAction,
    warning_code: warningCode,
    q,
    sort,
    limit,
    offset,
  });
  const result = await request('get', `runs/${seg(runId)}/extraction_rows${query}`);
  if (isSuccess(result.status) && result.data) {
    return { status: result.status, rows: result.data.rows || [], total: result.data.total ?? 0, error: null };
  }
  return { status: result.status, rows: [], total: 0, error: errorFrom(result.data, result.error, result.status) };
};

// GET /extraction_rows/:id — { row, catalog_item, validation, same_item, source_window, history }.
export const fetchExtractionRow = async (rowId) =>
  pick(await request('get', `extraction_rows/${seg(rowId)}`), 'detail', null);

const CORRECT_FALLBACKS = {
  422: 'The correction was rejected: an unknown or retired item, a value outside the vocabulary, or nothing changed.',
};

// POST /extraction_rows/:id/correct — { row, natural_key_clash, mapping_saved, mapping_note }.
export const correctExtractionRow = async (rowId, body) =>
  pick(await request('post', `extraction_rows/${seg(rowId)}/correct`, body), 'result', null, CORRECT_FALLBACKS);

// POST /extraction_rows/decide — cell-level exceptions, never carried over to the issuer.
export const decideExtractionRows = async ({ ids, action, reason }) => {
  const body = { ids, action };
  if (reason) body.reason = reason;
  return pick(await request('post', 'extraction_rows/decide', body), 'result', null);
};

// ---- unmapped rows -------------------------------------------------------------------------------

export const fetchUnmappedRows = async (runId, filters = {}) => {
  const { sheet, reason, isBlocking, decided, limit = DEFAULT_PAGE_SIZE, offset = 0 } = filters;
  const query = toQueryString({ sheet, reason, is_blocking: isBlocking, decided, limit, offset });
  const result = await request('get', `runs/${seg(runId)}/unmapped_rows${query}`);
  if (isSuccess(result.status) && result.data) {
    return { status: result.status, rows: result.data.rows || [], total: result.data.total ?? 0, error: null };
  }
  return { status: result.status, rows: [], total: 0, error: errorFrom(result.data, result.error, result.status) };
};

// GET /unmapped_rows/:id — { row, candidate_items, leftover, history }.
export const fetchUnmappedRow = async (rowId) =>
  pick(await request('get', `unmapped_rows/${seg(rowId)}`), 'detail', null);

const MAP_FALLBACKS = {
  400: 'Pick a catalog item or accept the proposed code.',
  409: 'This row cannot be mapped: leftover cells, a proposed code an admin rejected, or a retired item.',
  422: 'The mapping was rejected: an unknown item, no proposed code on this row, or a value outside the vocabulary.',
};

// POST /unmapped_rows/:id/map — { row, mapping_saved, mapping_note, applies: 'on rerun' }.
export const mapUnmappedRow = async (rowId, body) =>
  pick(await request('post', `unmapped_rows/${seg(rowId)}/map`, body), 'result', null, MAP_FALLBACKS);

// POST /unmapped_rows/:id/dismiss — for check rows and memos.
export const dismissUnmappedRow = async (rowId, { reason } = {}) =>
  pick(await request('post', `unmapped_rows/${seg(rowId)}/dismiss`, reason ? { reason } : undefined), 'result', null);

// ---- catalog and proposals -----------------------------------------------------------------------

export const fetchCatalogItems = async ({ q, section, status, limit = DEFAULT_PAGE_SIZE, offset = 0 } = {}) => {
  const result = await request('get', `catalog/items${toQueryString({ q, section, status, limit, offset })}`);
  if (isSuccess(result.status) && result.data) {
    const items = result.data.items || [];
    return { status: result.status, items, total: result.data.total ?? items.length, error: null };
  }
  return { status: result.status, items: [], total: 0, error: errorFrom(result.data, result.error, result.status) };
};

const CREATE_ITEM_FALLBACKS = {
  409: 'An item with that code already exists. A retired one can be re-activated by editing it.',
  422: 'The item was rejected: a missing or invalid field.',
};

// POST /catalog/items (admin) — 201 { item }.
export const createCatalogItem = async (body) =>
  pick(await request('post', 'catalog/items', body), 'item', null, CREATE_ITEM_FALLBACKS, 'item');

// PATCH /catalog/items/:code (admin) — { item }. `aliases` replaces the whole list.
export const updateCatalogItem = async (code, body) =>
  pick(
    await request('patch', `catalog/items/${encodeURIComponent(code)}`, body),
    'item',
    null,
    { 404: 'No catalog item has that code.' },
    'item'
  );

export const fetchProposals = async ({ status, limit = DEFAULT_PAGE_SIZE, offset = 0 } = {}) => {
  const result = await request('get', `catalog/proposals${toQueryString({ status, limit, offset })}`);
  if (isSuccess(result.status) && result.data) {
    const proposals = result.data.proposals || [];
    return { status: result.status, proposals, total: result.data.total ?? proposals.length, error: null };
  }
  return { status: result.status, proposals: [], total: 0, error: errorFrom(result.data, result.error, result.status) };
};

// POST /catalog/proposals/decide (admin) — { decided, skipped }. Only pending proposals are decided.
export const decideProposals = async ({ ids, decision }) =>
  pick(await request('post', 'catalog/proposals/decide', { ids, decision }), 'result', null);

// ---- audit ---------------------------------------------------------------------------------------

export const fetchAuditEvents = async (filters = {}) => {
  const {
    runId,
    subjectType,
    subjectId,
    eventType,
    actorType,
    from,
    to,
    limit = DEFAULT_PAGE_SIZE,
    offset = 0,
  } = filters;
  const query = toQueryString({
    run_id: runId,
    subject_type: subjectType,
    subject_id: subjectId,
    event_type: eventType,
    actor_type: actorType,
    from,
    to,
    limit,
    offset,
  });
  const result = await request('get', `audit${query}`);
  if (isSuccess(result.status) && result.data) {
    return { status: result.status, events: result.data.events || [], total: result.data.total ?? 0, error: null };
  }
  return { status: result.status, events: [], total: 0, error: errorFrom(result.data, result.error, result.status) };
};
